import React, { useMemo } from 'react';
import { resolveSiteStudyArea, type StudyAreaSite } from '../../lib/siteStudyArea';

/** Shared scope explanation only: no acreage deductions or campus fit model. */
export function LandScreeningBasis({ site, compact = false, onReviewArea, className = '' }: {
  site?: StudyAreaSite;
  compact?: boolean;
  onReviewArea?: () => void;
  className?: string;
}) {
  const selection = useMemo(() => {
    if (!site) return undefined;
    try { return { scope: resolveSiteStudyArea(site)?.scope, invalid: false }; }
    catch { return { scope: undefined, invalid: true }; }
  }, [site]);
  const content = <div className="space-y-2 text-xs leading-relaxed text-text-muted">
    {selection && <p data-testid="land-screening-selected-area">
      {selection.scope
        ? `${selection.scope.name} · ${selection.scope.grossAreaAcres.toLocaleString(undefined, { maximumFractionDigits: 2 })} gross acres selected.`
        : selection.invalid
          ? 'Study area needs review: a selected polygon changed or is unavailable.'
          : 'No development study area selected. Choose the polygon(s) for the DC + BTM project in Map.'}
    </p>}
    <p>Selected gross acreage defines the study extent. Usable land depends on mapped constraint coverage and reviewed exclusions; unknown areas remain unresolved.</p>
    <p>Test Fit checks the modeled generation components, entered setbacks and exclusions. It does not establish a complete DC + BTM campus fit.</p>
    <p>A combined layout still needs data halls, cooling and electrical infrastructure, shared access and drainage, plus permanent and temporary space by phase. An acreage balance alone cannot establish that everything fits.</p>
    {onReviewArea && <button type="button" onClick={onReviewArea} className="font-semibold text-primary underline underline-offset-2">
      {selection?.scope || selection?.invalid ? 'Review study area' : 'Select study area'}
    </button>}
  </div>;
  return compact
    ? <details className={className} data-testid="land-screening-basis">
      <summary className="cursor-pointer text-xs font-semibold text-text-primary">Land screening basis</summary>
      <div className="mt-2">{content}</div>
    </details>
    : <section className={`rounded border border-border bg-card p-3 ${className}`} aria-label="Land screening basis" data-testid="land-screening-basis">
      <h2 className="mb-2 text-sm font-semibold text-text-primary">Land screening basis</h2>
      {content}
    </section>;
}
